import React, { useState, useEffect } from 'react';
import { Product, Address, CartItem } from '../types';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import PaymentSetup from './PaymentSetup';
import Toast from '../components/Toast';

interface CartProps {
  onNavigate: (page: string) => void;
  onOrderCreated?: (orderId: string) => void;
  onSelectProduct?: (product: Product) => void;
}

const Cart: React.FC<CartProps> = ({ onNavigate, onOrderCreated, onSelectProduct }) => {
  const { cart, updateQuantity, removeFromCart, checkout } = useCart();
  const { isAuthenticated, user } = useAuth();
  
  // Checkout States
  const [checkoutStep, setCheckoutStep] = useState<'cart' | 'guest-info' | 'payment' | 'review'>('cart');
  const [processing, setProcessing] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
      setNotification({ message, type });
  };
  
  // Selection Logic
  const [selectedItemIds, setSelectedItemsIds] = useState<string[]>([]);

  useEffect(() => {
      // By default, select all items when cart loads
      setSelectedItemsIds(cart.map(i => i.id));
  }, [cart.length]);

  const toggleItemSelection = (id: string) => {
      setSelectedItemsIds(prev => 
          prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
      );
  };

  const selectedItems = cart.filter(i => selectedItemIds.includes(i.id));

  // Guest / Order Info
  const [guestInfo, setGuestInfo] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');

  // Calculations
  const calculateTotals = (items: CartItem[]) => {
      const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      
      // Calculate Discount (difference between originalPrice and price)
      const totalSavings = items.reduce((sum, item) => {
          if (item.onPromotion && item.discount) {
              const original = item.price / (1 - item.discount / 100);
              return sum + (original - item.price) * item.quantity;
          }
          return sum;
      }, 0);

      const tax = subtotal * 0.12;
      const total = subtotal + tax;
      return { subtotal, totalSavings, tax, total };
  };

  const totals = calculateTotals(selectedItems);

  const handleProductClick = (item: any) => {
    if (onSelectProduct) {
      onSelectProduct(item);
    }
  };

  const handleGuestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCheckoutStep('payment');
  };

  const handlePaymentSelection = (method: string) => {
    setSelectedPaymentMethod(method);
    setCheckoutStep('review');
  };

  const handlePlaceOrder = async () => {
    setProcessing(true);

    const userId = user?.id || `guest_${Date.now()}`;
    const businessName = user?.businessName || guestInfo.fullName;
    
    const shippingAddress: Address = user?.address ? {
       street: user.address,
       city: user.city || '',
       state: user.state || '',
       postalCode: user.zip || '',
       country: 'Canada',
       recipientName: user.contactName || user.businessName,
       phoneNumber: user.phone || '',
       preferredArrivalTime: 'Daytime'
    } : {
      street: guestInfo.address,
      city: guestInfo.city,
      state: guestInfo.state,
      postalCode: guestInfo.zip,
      country: "Canada",
      recipientName: guestInfo.fullName,
      phoneNumber: guestInfo.phone,
      preferredArrivalTime: "Daytime"
    };

    try {
      const order = await checkout(
        userId,
        businessName,
        shippingAddress,
        selectedPaymentMethod || "Credit Card",
        selectedItems
      );

      if (order && onOrderCreated) {
        onOrderCreated(order.id);
      } else {
        showToast("Order created successfully!");
        setTimeout(() => onNavigate('orders'), 1000);
      }
    } catch (err: any) {
      console.error("Checkout failed", err);
      showToast(err.message || "Failed to process checkout. Please try again.", "error");
    } finally {
      setProcessing(false);
    }
  };

  const startCheckout = () => {
    if (selectedItems.length === 0) {
        showToast("Please select at least one item to checkout", "info");
        return;
    }

    if (isAuthenticated) {
        setCheckoutStep('payment'); // Always allow choosing payment
    } else {
        setCheckoutStep('guest-info');
    }
  };

  // --- Render Steps ---

  if (checkoutStep === 'payment') {
      return (
          <div className="min-h-screen bg-slate-50 py-12">
               {notification && (
                    <Toast 
                        message={notification.message} 
                        type={notification.type} 
                        onClose={() => setNotification(null)} 
                    />
                )}
               <div className="max-w-4xl mx-auto px-4">
                  <div className="flex justify-between items-center mb-10">
                      <button onClick={() => setCheckoutStep(isAuthenticated ? 'cart' : 'guest-info')} className="text-slate-500 hover:text-slate-900 font-black uppercase text-xs tracking-widest flex items-center bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100 transition-all">
                        <span className="mr-2 text-lg">←</span> Back
                      </button>
                      <h2 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Choose Payment</h2>
                  </div>
                  <PaymentSetup 
                    onComplete={() => {}} 
                    onPaymentSelect={handlePaymentSelection}
                  />
               </div>
          </div>
      );
  }

  if (checkoutStep === 'guest-info') {
      return (
        <div className="min-h-screen bg-slate-50 py-12 flex items-center justify-center px-4">
            {notification && (
                <Toast 
                    message={notification.message} 
                    type={notification.type} 
                    onClose={() => setNotification(null)} 
                />
            )}
            <div className="max-w-2xl w-full bg-white rounded-[2.5rem] shadow-2xl p-12 border border-slate-100">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h2 className="text-4xl font-black text-slate-900 tracking-tight">Guest Checkout</h2>
                        <p className="text-slate-400 font-medium">Please provide your shipping information.</p>
                    </div>
                    <button onClick={() => setCheckoutStep('cart')} className="text-slate-300 hover:text-slate-900 transition-colors">
                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                
                <form onSubmit={handleGuestSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Full Name</label>
                            <input type="text" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                value={guestInfo.fullName} onChange={e => setGuestInfo({...guestInfo, fullName: e.target.value})} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Email Address</label>
                            <input type="email" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                value={guestInfo.email} onChange={e => setGuestInfo({...guestInfo, email: e.target.value})} />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Phone</label>
                            <input type="tel" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                value={guestInfo.phone} onChange={e => setGuestInfo({...guestInfo, phone: e.target.value})} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Address</label>
                            <input type="text" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                value={guestInfo.address} onChange={e => setGuestInfo({...guestInfo, address: e.target.value})} />
                        </div>
                         <div>
                            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">City</label>
                            <input type="text" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                value={guestInfo.city} onChange={e => setGuestInfo({...guestInfo, city: e.target.value})} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">State</label>
                                <input type="text" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                    value={guestInfo.state} onChange={e => setGuestInfo({...guestInfo, state: e.target.value})} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">ZIP</label>
                                <input type="text" required className="w-full px-6 py-4 bg-slate-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-500/10 font-bold"
                                    value={guestInfo.zip} onChange={e => setGuestInfo({...guestInfo, zip: e.target.value})} />
                            </div>
                        </div>
                    </div>
                    <button type="submit" className="w-full px-6 py-5 bg-slate-900 text-white rounded-2xl hover:bg-black font-black text-sm uppercase tracking-[0.3em] shadow-2xl transition-all">
                        Continue to Payment
                    </button>
                </form>
            </div>
        </div>
      );
  }

  if (checkoutStep === 'review') {
      return (
        <div className="min-h-screen bg-slate-50 py-12 px-4">
             {notification && (
                <Toast 
                    message={notification.message} 
                    type={notification.type} 
                    onClose={() => setNotification(null)} 
                />
            )}
             <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center mb-10">
                    <button onClick={() => setCheckoutStep('payment')} className="text-slate-500 hover:text-slate-900 font-black uppercase text-xs tracking-widest flex items-center bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100 transition-all">
                        <span className="mr-2 text-lg">←</span> Back to Payment
                    </button>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight">Final Review</h1>
                </div>
                
                <div className="grid gap-8">
                    {/* Summary Section */}
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 overflow-hidden">
                        <div className="p-10 border-b border-slate-50">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">Items to Purchase</h3>
                                <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">{selectedItems.length} Products</span>
                            </div>
                            <div className="space-y-4">
                                {selectedItems.map(item => {
                                    const originalPrice = (item.onPromotion && item.discount) 
                                        ? (item.price / (1 - item.discount / 100)) 
                                        : item.price;
                                    
                                    return (
                                        <div 
                                            key={item.id} 
                                            onClick={() => handleProductClick(item)}
                                            className="flex items-center justify-between cursor-pointer hover:bg-slate-50 p-2 rounded-xl transition-colors -mx-2"
                                        >
                                            <div className="flex items-center gap-4">
                                                <img src={item.image} className="w-12 h-12 rounded-xl object-cover border border-slate-100 shadow-sm" alt="" />
                                                <div>
                                                    <div className="font-black text-slate-900 text-sm group-hover:text-blue-600 transition-colors">{item.name}</div>
                                                    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Quantity: {item.quantity}</div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-mono font-black text-slate-900">${(item.price * item.quantity).toFixed(2)}</div>
                                                {item.onPromotion && (
                                                    <div className="text-[10px] text-slate-400 line-through font-bold">
                                                        ${(originalPrice * item.quantity).toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 border-b border-slate-50">
                            <div className="p-10 border-r border-slate-50">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4">Shipping Destination</h3>
                                <p className="text-slate-900 font-bold leading-relaxed">
                                    {isAuthenticated ? user?.businessName : guestInfo.fullName}<br/>
                                    <span className="font-medium text-slate-500">
                                        {isAuthenticated ? user?.address : guestInfo.address}<br/>
                                        {isAuthenticated ? `${user?.city}, ${user?.state} ${user?.zip}` : `${guestInfo.city}, ${guestInfo.state} ${guestInfo.zip}`}
                                    </span>
                                </p>
                            </div>
                            <div className="p-10">
                                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-4">Payment Method</h3>
                                <div className="flex items-center text-blue-600 font-black bg-blue-50/50 px-6 py-4 rounded-2xl border border-blue-100 uppercase text-xs tracking-widest">
                                    <span className="mr-3 text-xl">
                                        {selectedPaymentMethod === 'Amazon Pay' ? '🛒' : 
                                         selectedPaymentMethod === 'PayPal' ? '🅿️' : '📄'}
                                    </span> 
                                    {selectedPaymentMethod || 'Offline Payment'}
                                </div>
                            </div>
                        </div>

                        <div className="p-10 bg-slate-50/50 space-y-3">
                            <div className="flex justify-between text-sm font-bold text-slate-500">
                                <span>Subtotal</span>
                                <span className="font-mono">${totals.subtotal.toFixed(2)}</span>
                            </div>
                            {totals.totalSavings > 0 && (
                                <div className="flex justify-between text-sm font-black text-red-500 bg-red-50 px-4 py-2 rounded-xl">
                                    <span className="uppercase tracking-widest">🔥 Promotional Savings</span>
                                    <span className="font-mono">-${totals.totalSavings.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm font-bold text-slate-500">
                                <span>Applied Sales Tax (12%)</span>
                                <span className="font-mono">${totals.tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-end pt-4 border-t border-slate-200">
                                <span className="text-lg font-black text-slate-900 uppercase tracking-tighter">Total to Pay</span>
                                <span className="text-4xl font-black text-blue-600 font-mono tracking-tighter">${totals.total.toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    <button 
                        onClick={handlePlaceOrder}
                        disabled={processing}
                        className="w-full py-6 bg-blue-600 text-white rounded-[2rem] hover:bg-blue-700 disabled:bg-slate-200 font-black text-xl uppercase tracking-[0.4em] shadow-2xl shadow-blue-200 transition-all hover:-translate-y-1 active:scale-95"
                    >
                        {processing ? 'Authorizing...' : 'Place Secure Order'}
                    </button>
                </div>
             </div>
        </div>
      );
  }

  // Default: Cart View
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="text-8xl mb-8 animate-bounce-slow">🛒</div>
          <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight uppercase">Cart is Empty</h2>
          <p className="text-slate-400 font-medium mb-10 leading-relaxed">Your professional dental supplies are waiting. Discover our latest series now.</p>
          <button
            onClick={() => onNavigate('home')}
            className="w-full py-5 bg-slate-900 text-white rounded-2xl hover:bg-black font-black text-xs uppercase tracking-[0.3em] shadow-2xl transition-all"
          >
            Explore Catalog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-16">
      {notification && (
          <Toast 
              message={notification.message} 
              type={notification.type} 
              onClose={() => setNotification(null)} 
          />
      )}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-4">
            <div>
                <h1 className="text-5xl font-black text-slate-900 tracking-tight uppercase">Your Cart</h1>
                <p className="text-slate-400 font-medium mt-2">Manage your inventory selections before checkout.</p>
            </div>
            <div className="flex items-center gap-4">
                <button
                    onClick={() => setSelectedItemsIds(selectedItemIds.length === cart.length ? [] : cart.map(i => i.id))}
                    className="bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100 text-[10px] font-black uppercase tracking-widest text-blue-600 hover:bg-blue-50 transition-colors"
                >
                    {selectedItemIds.length === cart.length ? 'Deselect All' : 'Select All'}
                </button>
                <div className="bg-white px-6 py-3 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Items:</span>
                    <span className="text-lg font-black text-slate-900">{cart.length}</span>
                </div>
            </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-10 items-start">
            {/* Items List */}
            <div className="lg:col-span-2 space-y-4">
                {cart.map((item) => (
                    <div key={item.id} className={`bg-white rounded-3xl p-6 shadow-sm border transition-all flex items-center gap-6 group hover:shadow-md ${selectedItemIds.includes(item.id) ? 'border-blue-200 ring-2 ring-blue-500/5' : 'border-slate-100'}`}>
                        {/* Selector */}
                        <button 
                            onClick={() => toggleItemSelection(item.id)}
                            className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all ${selectedItemIds.includes(item.id) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-slate-50 text-transparent border border-slate-200 hover:border-blue-400'}`}
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        </button>

                        <div onClick={() => handleProductClick(item)} className="cursor-pointer relative flex-shrink-0">
                            <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded-2xl border border-slate-50 shadow-sm group-hover:scale-105 transition-transform" />
                            {item.onPromotion && <span className="absolute -top-2 -left-2 bg-red-500 text-white text-[8px] font-black uppercase px-2 py-1 rounded-full shadow-lg">Sale</span>}
                        </div>

                        <div className="flex-1 min-w-0">
                            <h3 onClick={() => handleProductClick(item)} className="font-black text-slate-900 text-lg mb-1 cursor-pointer hover:text-blue-600 transition-colors truncate">{item.name}</h3>
                            <div className="flex items-center gap-3">
                                <span className="font-mono font-bold text-slate-400 text-xs tracking-tighter">${item.price.toFixed(2)} / unit</span>
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-300">• {item.category}</span>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-4">
                            <div className="flex items-center bg-slate-50 p-1.5 rounded-xl border border-slate-100">
                                <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors font-black">-</button>
                                <span className="w-10 text-center font-black text-sm">{item.quantity}</span>
                                <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors font-black">+</button>
                            </div>
                            <button onClick={() => removeFromCart(item.id)} className="text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-red-500 transition-colors">Delete</button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Sticky Sidebar */}
            <div className="sticky top-24">
                <div className="bg-white rounded-[2.5rem] p-10 shadow-xl border border-slate-100">
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-8">Purchase Summary</h2>
                    
                    <div className="space-y-4 mb-10">
                        <div className="flex justify-between text-sm font-bold text-slate-400">
                            <span>Selected Subtotal</span>
                            <span className="text-slate-900">${totals.subtotal.toFixed(2)}</span>
                        </div>
                        {totals.totalSavings > 0 && (
                            <div className="flex justify-between text-[10px] font-black text-red-500 bg-red-50 px-4 py-2 rounded-xl uppercase tracking-widest">
                                <span>Discount Benefit</span>
                                <span>-${totals.totalSavings.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-sm font-bold text-slate-400">
                            <span>Est. BC Tax (12%)</span>
                            <span className="text-slate-900">${totals.tax.toFixed(2)}</span>
                        </div>
                        <div className="pt-6 border-t border-slate-100 flex justify-between items-end">
                            <span className="text-xs font-black uppercase tracking-widest text-slate-400">Final Price</span>
                            <span className="text-3xl font-black text-blue-600 tracking-tighter">${totals.total.toFixed(2)}</span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <button
                            onClick={startCheckout}
                            disabled={processing || selectedItems.length === 0}
                            className="w-full py-6 bg-slate-900 text-white rounded-2xl hover:bg-black disabled:bg-slate-100 disabled:text-slate-300 transition-all font-black text-sm uppercase tracking-[0.3em] shadow-2xl hover:-translate-y-1 active:scale-95"
                        >
                            Proceed to Checkout
                        </button>
                        
                        {!isAuthenticated && (
                            <button
                                onClick={() => onNavigate('login')}
                                className="w-full py-5 border-2 border-slate-100 text-slate-900 rounded-2xl hover:bg-slate-50 transition-all font-black text-[10px] uppercase tracking-[0.2em]"
                            >
                                Sign In for Business Rates
                            </button>
                        )}
                    </div>

                    <div className="mt-8 pt-8 border-t border-slate-50 flex items-center justify-center gap-4 opacity-30">
                        <span className="text-xl">🛡️</span>
                        <span className="text-[10px] font-black uppercase tracking-widest">SECURE CHECKOUT</span>
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;